import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TermsConditionsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-12" data-testid="terms-conditions-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Înapoi la pagina principală
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 font-['Archivo']">
                Termeni și Condiții
              </h1>
              <p className="text-slate-500">Ultima actualizare: Martie 2026</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <h2>1. Definiții</h2>
            <p>
              <strong>"Platforma"</strong> se referă la website-ul AUTO CORA și toate serviciile 
              asociate.<br />
              <strong>"Utilizator"</strong> reprezintă orice persoană care accesează sau utilizează 
              Platforma.<br />
              <strong>"Servicii"</strong> includ cursurile video, testele, materialele educaționale 
              și toate funcționalitățile oferite.
            </p>

            <h2>2. Acceptarea Termenilor</h2>
            <p>
              Prin accesarea și utilizarea Platformei, acceptați să fiți obligat de acești Termeni 
              și Condiții. Dacă nu sunteți de acord cu oricare dintre termeni, vă rugăm să nu 
              utilizați serviciile noastre.
            </p>

            <h2>3. Descrierea Serviciilor</h2>
            <p>AUTO CORA oferă:</p>
            <ul>
              <li>Cursuri video pentru pregătirea examenului auto</li>
              <li>Teste și chestionare similare examenului DRPCIV</li>
              <li>Materiale educaționale pentru legislația rutieră</li>
              <li>Sistem de urmărire a progresului</li>
              <li>Servicii de școală auto (programări, categorii permis)</li>
            </ul>

            <h2>4. Înregistrarea și Contul</h2>
            <p>
              Pentru a accesa anumite funcționalități, trebuie să vă creați un cont. Sunteți 
              responsabil pentru:
            </p>
            <ul>
              <li>Furnizarea de informații corecte și actualizate</li>
              <li>Păstrarea confidențialității parolei</li>
              <li>Toate activitățile desfășurate din contul dumneavoastră</li>
            </ul>

            <h2>5. Abonamente și Plăți</h2>
            <p>
              Anumite servicii necesită un abonament plătit. Prin achiziționarea unui abonament:
            </p>
            <ul>
              <li>Acceptați prețul afișat la momentul achiziției</li>
              <li>Plățile sunt procesate securizat prin <strong>Stripe</strong></li>
              <li>Abonamentele se reînnoiesc automat, dacă nu sunt anulate</li>
              <li>Puteți anula abonamentul oricând din setările contului</li>
            </ul>

            <h3>5.1. Politica de Rambursare</h3>
            <p>
              Oferim rambursare integrală în primele 14 zile de la achiziție, dacă nu ați 
              utilizat mai mult de 20% din conținutul cursului. Pentru solicitări de rambursare, 
              contactați-ne prin pagina de Contact.
            </p>

            <h3>5.2. Procesarea Plăților</h3>
            <p>
              Plățile sunt procesate prin <strong>Stripe</strong>, un furnizor de plăți certificat 
              PCI-DSS. Nu stocăm datele cardului dumneavoastră pe serverele noastre. Pentru 
              detalii despre cum Stripe procesează datele, consultați 
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"> Politica de Confidențialitate Stripe</a>.
            </p>

            <h2>6. Utilizare Acceptabilă</h2>
            <p>Vă angajați să nu:</p>
            <ul>
              <li>Partajați contul sau credențialele cu alte persoane</li>
              <li>Copiați, distribuiți sau reproduceți conținutul fără permisiune</li>
              <li>Utilizați Platforma în scopuri ilegale</li>
              <li>Încercați să accesați neautorizat sistemele noastre</li>
              <li>Înregistrați sau capturați conținutul video</li>
            </ul>

            <h2>7. Proprietate Intelectuală</h2>
            <p>
              Tot conținutul Platformei (texte, video-uri, grafică, logo-uri) este protejat de 
              drepturile de autor și aparține AUTO CORA sau licențiatorilor săi. Nu aveți dreptul 
              de a reproduce, distribui sau crea lucrări derivate fără acordul nostru scris.
            </p>

            <h2>8. Limitarea Răspunderii</h2>
            <p>
              Platforma este oferită "așa cum este". Nu garantăm că:
            </p>
            <ul>
              <li>Serviciile vor fi neîntrerupte sau fără erori</li>
              <li>Veți promova examenul auto doar prin utilizarea platformei</li>
              <li>Conținutul este complet sau actualizat la zi</li>
            </ul>
            <p>
              AUTO CORA nu este responsabilă pentru rezultatele la examenul auto. Platforma 
              este un instrument de pregătire suplimentar.
            </p>

            <h2>9. Suspendarea și Încetarea</h2>
            <p>
              Ne rezervăm dreptul de a suspenda sau închide contul dumneavoastră în cazul 
              încălcării acestor termeni, fără notificare prealabilă și fără rambursare.
            </p>

            <h2>10. Modificări ale Termenilor</h2>
            <p>
              Putem actualiza acești termeni periodic. Continuarea utilizării Platformei după 
              modificări constituie acceptarea noilor termeni.
            </p>

            <h2>11. Legislație Aplicabilă</h2>
            <p>
              Acești Termeni și Condiții sunt guvernați de legislația din România. Orice dispută 
              va fi soluționată de instanțele competente din România.
            </p>

            <h2>12. Contact</h2>
            <p>
              Pentru întrebări despre acești termeni, ne puteți contacta prin pagina de{" "}
              <Link to="/contact" className="text-blue-600 hover:underline">Contact</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
